import moment from 'moment';

import { portalAction } from 'root/redux/action';
import notification from 'root/util/notification';
import { PROVIDER_CATEGORY, PROVIDER_CATEGORY_PREFIX } from 'root/util/constants';
import commonUtil from 'root/util/common-util';
import memberService from 'root/util/member-service';
import { PROVIDER_URL } from 'root/util/constants';
import { gu_filterCommonKey, gu_proceedNestedObjectWithVM } from 'root/util/general-util';

const checkTimeRange = ({ startTime, endTime }) => {
    const now = moment();

    if (startTime) {
        const start = moment(startTime, 'DD-MM-YYYY HH:mm:ss', true);
        if (!start.isValid() || now.isBefore(start)) return false;
    }

    if (endTime) {
        const end = moment(endTime, 'DD-MM-YYYY HH:mm:ss', true);
        if (!end.isValid() || now.isAfter(end)) return false;
    }

    return true;
};

const resolveProviderParam = (originProviderParam, defaultProvider) => {
    if (typeof originProviderParam === 'string') {
        return originProviderParam || defaultProvider;
    }
    return gu_filterCommonKey(originProviderParam) || defaultProvider;
};

const normalizeConfigs = (configs) => {
    if (!configs) return [];
    return Array.isArray(configs) ? configs : [configs];
};

const findMatchingConfig = (configs, providerParam, vm) => {
    const merchantCode = window.merchantCode;
    const currency = vm.props?.language?.currencyLang;

    return configs.find((config) => {
        if (!config) return false;

        return isValidProvider(config, providerParam) && isValidMerchant(config, merchantCode) && isValidCurrency(config, currency) && checkTimeRange(config);
    });
};

const toAppliedArray = (object) => {
    if (typeof object !== 'object' || !object) return object;

    return Array.isArray(object) ? object : Object.keys(object).filter((key) => object[key]);
};

const isValidMerchant = (config, merchantCode) => {
    const includeMerchants = toAppliedArray(config?.includeMerchants);
    const excludeMerchants = toAppliedArray(config?.excludeMerchants);

    if (includeMerchants?.length && !includeMerchants.includes(merchantCode)) {
        return false;
    }

    if (excludeMerchants?.length && excludeMerchants.includes(merchantCode)) {
        return false;
    }

    return true;
};

const isValidCurrency = (config, currency) => {
    const { includeCurrencies, excludeCurrencies } = config;

    if (includeCurrencies?.length && !includeCurrencies.includes(currency)) {
        return false;
    }
    if (excludeCurrencies?.length && excludeCurrencies.includes(currency)) {
        return false;
    }
    return true;
};

const isValidProvider = (config, providerParam) => {
    return !config.fromProvider || config.fromProvider === providerParam;
};

const getCorrectProviderParam = (vm, originProviderParam, defaultProvider = 'IBC') => {
    const providerParam = resolveProviderParam(originProviderParam, defaultProvider);
    const configs = normalizeConfigs(gu_proceedNestedObjectWithVM(vm, vm.props.flagsmith?.hotMatchProviderOverrides));
    const matchingConfig = findMatchingConfig(configs, providerParam, vm);

    const isSupportedProvider = vm.props.menuSlice?.processedBaseMenuSettings?.subCategoryMenus?.[matchingConfig?.menu]?.some?.(
        (p) => p?.provider === matchingConfig?.toProvider
    );
    return (isSupportedProvider && matchingConfig?.toProvider) || providerParam;
};

const provider = {
    providers: {
        MML: {
            transferSettingIcon: '/public/html/images/footer/mml_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mml_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mml_of.png',
            menuName: 'MarbleMagic',
        },
        YOB: {
            transferSettingIcon: '/public/html/images/footer/yob_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_yob_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_yob_of.png',
            menuName: 'YouBetLotto',
        },
        CHG: {
            transferSettingIcon: '/public/html/images/footer/chg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_chg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_chg_of.png',
            menuName: 'ChoiceGaming',
        },
        PGSM: {
            transferSettingIcon: '/public/html/images/footer/pgsm_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pgsm_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pgsm_of.png',
            menuName: 'PGSM',
        },
        RT2: {
            transferSettingIcon: '/public/html/images/footer/redtiger_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_redtiger_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_redtiger_of.png',
            menuName: 'RedTiger2',
        },
        CPG: {
            transferSettingIcon: '/public/html/images/footer/cpg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_cpg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_cpg_of.png',
            menuName: 'CPGames',
        },
        G168: {
            transferSettingIcon: '/public/html/images/footer/g168_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_g168_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_g168_of.png',
            menuName: '168g',
        },
        MS: {
            transferSettingIcon: '/public/html/images/footer/ms_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ms_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ms_of.png',
            menuName: 'Microslot',
        },
        L365: {
            transferSettingIcon: '/public/html/images/footer/l365_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_l365_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_l365_of.png',
            menuName: 'Lucky365',
        },
        AVX: {
            transferSettingIcon: '/public/html/images/footer/avx_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_avx_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_avx_of.png',
            menuName: 'Aviatrix',
        },
        EW: {
            transferSettingIcon: '/public/html/images/footer/ew_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ew_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ew_of.png',
            menuName: 'EpicWins',
        },
        AW: {
            transferSettingIcon: '/public/html/images/footer/aw_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_aw_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_aw_of.png',
            menuName: 'ACEWIN',
        },
        AFB: {
            transferSettingIcon: '/public/html/images/footer/a288_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_a288_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_a288_of.png',
            menuName: 'AFB288',
        },
        BTR: {
            transferSettingIcon: '/public/html/images/footer/btr_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_btr_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_btr_of.png',
            menuName: 'Beter',
        },
        MTL: {
            transferSettingIcon: '/public/html/images/footer/mtl_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mtl_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mtl_of.png',
            menuName: 'MTLive',
        },
        ATG: {
            transferSettingIcon: '/public/html/images/footer/atg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_atg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_atg_of.png',
            menuName: 'AgileTitanGaming',
        },
        IBEX: {
            transferSettingIcon: '/public/html/images/footer/ibex_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ibex_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ibex_of.png',
            menuName: 'IBEX',
        },
        PTG: {
            transferSettingIcon: '/public/html/images/footer/ptg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ptg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ptg_of.png',
            menuName: 'PrettyGaming',
        },
        VMG: {
            transferSettingIcon: '/public/html/images/footer/vmg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_vmg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_vmg_of.png',
            menuName: 'VMG',
        },
        IMSPO: {
            transferSettingIcon: '/public/html/images/footer/imspo_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_imspo_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_imspo_of.png',
            menuName: 'IMSports',
        },
        BNG: {
            transferSettingIcon: '/public/html/images/footer/bng_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_bng_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_bng_of.png',
            menuName: 'Boongo',
        },
        HS2: {
            transferSettingIcon: '/public/html/images/footer/hs2_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_hs2_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_hs2_of.png',
            menuName: 'HacksawGaming2',
        },
        WESLT: {
            transferSettingIcon: '/public/html/images/footer/weslt_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_weslt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_weslt_of.png',
            menuName: 'WE Slots',
        },
        HS: {
            transferSettingIcon: '/public/html/images/footer/hs_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_hs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_hs_of.png',
            menuName: 'HacksawGaming',
        },
        KMS: {
            transferSettingIcon: '/public/html/images/footer/kms_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_kms_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_kms_of.png',
            menuName: 'KingMidas',
        },
        TKS: {
            transferSettingIcon: '/public/html/images/footer/tks_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_tks_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_tks_of.png',
            menuName: 'Tangkas',
        },
        JDB2: {
            transferSettingIcon: '/public/html/images/footer/jdb2_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_jdb2_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_jdb2_of.png',
            menuName: 'JDB2',
        },
        VTP: {
            transferSettingIcon: '/public/html/images/footer/vtp_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_vtp_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_vtp_of.png',
            menuName: 'VertexPlay',
        },
        POP: {
            transferSettingIcon: '/public/html/images/footer/pop_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pop_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pop_of.png',
            menuName: 'PPPOP',
        },
        explaination: {
            launchFromMenu: 'format: category-menuName',
            transferSettingIcon: 'setting page there use de transfer icon',
            footerIcon: 'footer use punya icon path, ordering based on top down of this json',
            menuName: 'menu name in menu-settings.json',
        },
        BTI: {
            transferSettingIcon: '/public/html/images/footer/bti_hover.png',
            menuName: 'BTI',
            providerCode: 'BTI2',
            footerIcon: '/public/html/images/footer/ftr_brand_bti_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_bti_of.png',
        },
        SG: {
            launchFromMenu: ['F-SG Fishing', 'F-SG FishingWar'],
            transferSettingIcon: '/public/html/images/footer/spadegaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_spade_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_spade_of.png',
            menuName: 'SpadeGaming',
        },
        PT: {
            launchFromMenu: ['C-PlayTech', 'F-PT Fishing'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            menuName: 'PT2',
        },
        PT2: {
            launchFromMenu: ['C-PT2'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PT2',
        },
        PT3: {
            launchFromMenu: ['SL-PT3'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PT3',
        },
        PT4: {
            launchFromMenu: ['SL-PT4'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PT4',
        },
        PT5: {
            launchFromMenu: ['SL-PT5'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PT5',
        },
        PTL: {
            launchFromMenu: ['C-PTL'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PTL',
        },
        PTL5: {
            launchFromMenu: ['C-PTL5'],
            transferSettingIcon: '/public/html/images/footer/playtech_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pt_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_playtech.png',
            menuName: 'PTL5',
        },
        MG: {
            transferSettingIcon: '/public/html/images/footer/microgaming_hover.png',
        },
        MGP: {
            launchFromMenu: ['C-MicroGamingPlus'],
            transferSettingIcon: '/public/html/images/footer/microgaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mg_of.png',
            menuName: 'MicroGamingPlus',
        },
        MGPL: {
            launchFromMenu: ['C-Microgaming'],
            transferSettingIcon: '/public/html/images/footer/microgaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mg_of.png',
            menuName: 'Microgaming',
        },
        TTG: {
            transferSettingIcon: '/public/html/images/footer/toptrend_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ttg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ttg_of.png',
            menuName: 'TTG',
        },
        GP: {
            launchFromMenu: [
                'C-GamePlay',
                'L-GamePlay Fast3 Lottery',
                'L-GamePlay Keno Lottery',
                'L-GamePlay Ladder Lottery',
                'L-GamePlay Rps Lottery',
                'L-GamePlay Sode Lottery',
                'L-GamePlay Taixiu Lottery',
                'L-GamePlay Thor Lottery',
                'L-GamePlay Lucky Derby Lottery',
                'L-GamePlay Pk10 Lottery',
                'L-GamePlay Thai Lottery',
            ],
            transferSettingIcon: '/public/html/images/footer/gameplay_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_gp_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_gp_of.png',
            menuName: 'GamePlay',
        },
        AG: {
            launchFromMenu: ['C-AsiaGaming'],
            transferSettingIcon: '/public/html/images/footer/asiagaming_hover.png',
        },
        AG2: {
            launchFromMenu: ['C-AsiaGaming2'],
            transferSettingIcon: '/public/html/images/footer/asiagaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ag_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ag_of.png',
            menuName: 'PlayAce',
        },
        HB: {
            transferSettingIcon: '/public/html/images/footer/habanero_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_habanero_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_habanero_of.png',
            menuName: 'Habanero',
        },
        SCR: {
            transferSettingIcon: '/public/html/images/footer/918kiss_hover.png',
        },
        SCR2: {
            transferSettingIcon: '/public/html/images/footer/918kiss_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_scr_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_scr_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_918kiss.png',
            menuName: '918Kiss',
        },
        AB: {
            launchFromMenu: ['C-Allbet'],
            transferSettingIcon: '/public/html/images/footer/allbet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_allbet_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_allbet_of.png',
            menuName: 'Allbet',
        },
        AB2: {
            launchFromMenu: ['C-Allbet'],
            transferSettingIcon: '/public/html/images/footer/allbet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_allbet_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_allbet_of.png',
            menuName: 'Allbet2',
        },
        GD: {
            launchFromMenu: ['C-GoldDeluxe'],
            transferSettingIcon: '/public/html/images/footer/golddeluxe_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_gd_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_gd_of.png',
            menuName: 'GoldDeluxe',
        },
        DG: {
            launchFromMenu: ['C-DreamGaming'],
            transferSettingIcon: '/public/html/images/footer/dreamgaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_dream_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_dream_of.png',
            menuName: 'DreamGaming',
        },
        BMK: {
            launchFromMenu: ['C-BMK'],
            transferSettingIcon: '/public/html/images/footer/evolutiongaming.png',
        },
        BMK2: {
            launchFromMenu: ['C-Bamako2'],
            transferSettingIcon: '/public/html/images/footer/evolutiongaming.png',
        },
        EVO: {
            launchFromMenu: ['C-Evo'],
            transferSettingIcon: '/public/html/images/footer/evolutiongaming.png',
            footerIcon: '/public/html/images/footer/ftr_brand_evo_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_evo_of.png',
            menuName: 'Evo',
        },
        GG: {
            launchFromMenu: ['F-GG Fishing'],
            transferSettingIcon: '/public/html/images/footer/gggaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ggg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ggg_of.png',
            menuName: 'GG Fishing',
        },
        IDN: {
            launchFromMenu: ['menu-Poker'],
            transferSettingIcon: '/public/html/images/footer/idn_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_idn_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_idn_of.png',
            menuName: 'Poker',
        },
        CMD: {
            launchFromMenu: ['S-CMD368'],
            transferSettingIcon: '/public/html/images/footer/cmd368_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_cmd_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_cmd_of.png',
            menuName: 'CMD368',
        },
        BR: {
            launchFromMenu: ['S-BetRadar'],
            transferSettingIcon: '/public/html/images/footer/betradar_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_br_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_br_of.png',
            menuName: 'BetRadar',
        },
        IBC: {
            transferSettingIcon: '/public/html/images/footer/maxbet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_maxbet_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_maxbet_of.png',
            menuName: 'IBC',
        },
        SBO: {
            launchFromMenu: ['S-SBO'],
            transferSettingIcon: '/public/html/images/footer/sbobet_hover.png',
            footerIcon: '/public/html/images/footer/sbobet_hover.png',
            footerIconOf: '/public/html/images/footer/sbobet.png',
            menuName: 'SBO',
        },
        SBO2: {
            launchFromMenu: ['S-SBO'],
            transferSettingIcon: '/public/html/images/footer/sbobet_hover.png',
            footerIcon: '/public/html/images/footer/sbobet_hover.png',
            footerIconOf: '/public/html/images/footer/sbobet.png',
            menuName: 'SBO',
        },
        UG: {
            launchFromMenu: ['C-SexyBaccarat'],
            transferSettingIcon: '/public/html/images/footer/sexybaccarat_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_aesexy_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_aesexy_of.png',
            menuName: 'SexyBaccarat',
        },
        AES: {
            launchFromMenu: ['C-SexyBaccarat'],
            transferSettingIcon: '/public/html/images/footer/sexybaccarat_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_aesexy_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_aesexy_of.png',
            menuName: 'SexyBaccarat2',
        },
        AES2: {
            launchFromMenu: ['C-SexyBaccarat'],
            transferSettingIcon: '/public/html/images/footer/sexybaccarat_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_aesexy_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_aesexy_of.png',
            menuName: 'SexyBaccarat2',
        },
        MEGA: {
            transferSettingIcon: '/public/html/images/footer/mega888_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mega_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mega_of.png',
            menuName: 'Mega', // ori is Mega888 but its only use on menu tab, submenu is Mega.
        },
        UFA: {
            transferSettingIcon: '/public/html/images/footer/ufabet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ufabet_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ufabet_of.png',
            menuName: 'UFA',
        },
        PP: {
            transferSettingIcon: '/public/html/images/footer/pragmaticplay_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pp_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pp_of.png',
            menuName: 'PragmaticPlay',
        },
        PG: {
            transferSettingIcon: '/public/html/images/footer/playngo_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_playngo_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_playngo_of.png',
            menuName: 'PlayNGo',
        },
        PNG2: {
            transferSettingIcon: '/public/html/images/footer/playngo2_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_playngo2_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_playngo2_of.png',
            menuName: 'PlayNGo2',
        },
        EBET: {
            launchFromMenu: ['C-EBET'],
            transferSettingIcon: '/public/html/images/footer/ebet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ebet_of.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ebet_ov.png',
            menuName: 'EBET',
        },
        IME: {
            transferSettingIcon: '/public/html/images/footer/ime_hover.png',
            footerIcon: '/public/html/images/footer/ime_hover.png',
            menuName: 'IME',
        },
        NS: {
            transferSettingIcon: '/public/html/images/footer/nextspin_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_nextspin_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_nextspin_of.png',
            menuName: 'NextSpin',
        },
        SA: {
            launchFromMenu: ['C-SAGaming', 'F-SA Fishing'],
            transferSettingIcon: '/public/html/images/footer/sagaming_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_sa_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_sa_of.png',
            jackpotIcon: '/public/html/images/home_img/provider_sa_gaming.png',
            menuName: 'SAGaming',
        },
        QQK: {
            launchFromMenu: ['L-QQKeno Lottery', 'L-QQKenoThai Lottery'],
            transferSettingIcon: '/public/html/images/footer/qqkeno_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_qqkeno_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_qqkeno_of.png',
            menuName: 'QQKeno Lottery',
        },
        M8: {
            launchFromMenu: ['S-M8'],
            transferSettingIcon: '/public/html/images/footer/m8bet_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_m8bet_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_m8bet_of.png',
            menuName: 'M8',
        },
        KM: {
            transferSettingIcon: '/public/html/images/footer/km_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_km_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_km_of.png',
            menuName: 'KingMaker',
        },
        KM2: {
            transferSettingIcon: '/public/html/images/footer/km_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_km_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_km_of.png',
            menuName: 'KingMaker',
        },
        WM: {
            transferSettingIcon: '/public/html/images/footer/wm_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_wm_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_wm_of.png',
            menuName: 'WMCasino',
        },
        QQT: {
            transferSettingIcon: '/public/html/images/footer/qqkeno_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_qqthai_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_qqthai_of.png',
            menuName: 'QQKenoThai Lottery',
        },
        PGS: {
            transferSettingIcon: '/public/html/images/footer/pgsoft_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pgs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pgs_of.png',
            menuName: 'PGSoft',
        },
        PGS3: {
            transferSettingIcon: '/public/html/images/footer/pgsoft_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pgs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pgs_of.png',
            menuName: 'PGSoft',
        },
        PGS2: {
            transferSettingIcon: '/public/html/images/footer/pgs2t_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pgs2_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pgs2_of.png',
            menuName: 'PGSlot',
        },
        FG: {
            transferSettingIcon: '/public/html/images/footer/funkygames_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_fg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_fg_of.png',
            menuName: 'FunkyGames',
        },
        JKR: {
            transferSettingIcon: '/public/html/images/footer/pvdjoker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_joker_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_joker_of.png',
            menuName: 'Joker',
        },
        NT: {
            transferSettingIcon: '/public/html/images/footer/pvdjoker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_netent_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_netent_of.png',
            menuName: 'NETENT',
        },
        JL: {
            transferSettingIcon: '/public/html/images/footer/pvdjoker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_jili_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_jili_of.png',
            menuName: 'JILI',
        },
        RT: {
            transferSettingIcon: '/public/html/images/footer/redtiger_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_redtiger_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_redtiger_of.png',
            menuName: 'RedTiger',
        },
        T7: {
            transferSettingIcon: '/public/html/images/footer/pvdjoker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_t7_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_t7_of.png',
            menuName: 'T7',
        },
        CMDL: {
            transferSettingIcon: '/public/html/images/footer/pvdjoker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_cmdlottery_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_cmdlottery_of.png',
            menuName: 'CmdLottery',
        },
        V8: {
            transferSettingIcon: '/public/html/images/footer/v8poker_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_v8poker_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_v8poker_of.png',
            menuName: 'V8Poker',
        },
        RSG: {
            transferSettingIcon: '/public/html/images/footer/rsg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_rsg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_rsg_of.png',
            menuName: 'RoyalSlotGaming',
        },
        PSY8: {
            transferSettingIcon: '/public/html/images/footer/psy8_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_psy8_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_psy8_of.png',
            menuName: 'Pussy888',
        },
        CQ9: {
            transferSettingIcon: '/public/html/images/footer/cq9_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_cq9_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_cq9_of.png',
            menuName: 'CQ9',
        },
        FC: {
            transferSettingIcon: '/public/html/images/footer/fc_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_fc_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_fc_of.png',
            menuName: 'FaChai',
        },
        GW: {
            transferSettingIcon: '/public/html/images/footer/gw_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_gw_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_gw_of.png',
            menuName: 'Shi Cai',
        },
        BINS2: {
            transferSettingIcon: '/public/html/images/footer/bins_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_bins_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_bins_of.png',
            menuName: 'BBIN2',
        },
        BINL2: {
            transferSettingIcon: '/public/html/images/footer/binl_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_binl_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_binl_of.png',
            menuName: 'BBIN2',
        },
        TCG: {
            transferSettingIcon: '/public/html/images/footer/tcg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_tcg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_tcg_of.png',
            menuName: 'TC Gaming',
        },
        ED: {
            transferSettingIcon: '/public/html/images/footer/ed_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ed_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ed_of.png',
            menuName: 'EdayLotto',
        },
        MT: {
            transferSettingIcon: '/public/html/images/footer/mt_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mt_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mt_of.png',
            menuName: 'MiniTed',
        },
        TF: {
            transferSettingIcon: '/public/html/images/footer/tf_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_tf_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_tf_of.png',
            menuName: 'TFG',
        },
        YGR: {
            transferSettingIcon: '/public/html/images/footer/ygr_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ygr_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ygr_of.png',
            menuName: 'YesGetRich',
        },
        HC: {
            transferSettingIcon: '/public/html/images/footer/hc_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_hc_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_hc_of.png',
            menuName: 'HCSlot',
        },
        PS: {
            transferSettingIcon: '/public/html/images/footer/ps_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ps_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ps_of.png',
            menuName: 'PlayStar',
        },
        SPX: {
            transferSettingIcon: '/public/html/images/footer/spx_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_spx_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_spx_of.png',
            menuName: 'Spinix',
        },
        WS: {
            transferSettingIcon: '/public/html/images/footer/ws_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ws_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ws_of.png',
            menuName: 'WS168',
        },
        AOG: {
            transferSettingIcon: '/public/html/images/footer/aog_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_aog_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_aog_of.png',
            menuName: 'AOG',
        },
        W: {
            transferSettingIcon: '/public/html/images/footer/w_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_w_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_w_of.png',
            menuName: 'WCasino',
        },
        TPG: {
            transferSettingIcon: '/public/html/images/footer/tpg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_tpg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_tpg_of.png',
            menuName: 'TPG',
        },
        PINN: {
            transferSettingIcon: '/public/html/images/footer/pinn_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_pinn_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_pinn_of.png',
            menuName: 'PINN',
        },
        YGG: {
            transferSettingIcon: '/public/html/images/footer/ygg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ygg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ygg_of.png',
            menuName: 'YGGDrasil',
        },
        EP: {
            transferSettingIcon: '/public/html/images/footer/ep_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ep_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ep_of.png',
            menuName: 'EP',
        },
        WE: {
            transferSettingIcon: '/public/html/images/footer/ep_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ep_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ep_of.png',
            menuName: 'WE',
        },
        DS: {
            transferSettingIcon: '/public/html/images/footer/ds_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ds_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ds_of.png',
            menuName: 'DragoonSoft',
        },
        YBL: {
            transferSettingIcon: '/public/html/images/footer/ybl_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ybl_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ybl_of.png',
            menuName: 'YBL',
        },
        JS: {
            transferSettingIcon: '/public/html/images/footer/js_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_js_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_js_of.png',
            menuName: 'Jolly Sports',
        },
        AP: {
            transferSettingIcon: '/public/html/images/footer/ap_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ap_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ap_of.png',
            menuName: 'AdvantPlay',
        },
        AMBS: {
            menuName: 'Askmeslot',
            footerIcon: '/public/html/default_whitelabel/template/images/footer-icon/provider/s2/Askmeslot.png',
        },
        VC: {
            transferSettingIcon: '/public/html/images/footer/vc_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_vc_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_vc_of.png',
            menuName: 'ViaCasino',
        },
        EZ: {
            transferSettingIcon: '/public/html/images/footer/ez_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ez_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ez_of.png',
            menuName: 'Ezugi',
        },
        R88: {
            transferSettingIcon: '/public/html/images/footer/r88_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_r88_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_r88_of.png',
            menuName: 'Rich88',
        },
        EXS: {
            transferSettingIcon: '/public/html/images/footer/exs_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_exs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_exs_of.png',
            menuName: 'EXS',
        },
        NW: {
            transferSettingIcon: '/public/html/images/footer/nw_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_nw_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_nw_of.png',
            menuName: '9Wickets',
        },
        SW: {
            transferSettingIcon: '/public/html/images/footer/sw_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_sw_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_sw_of.png',
            menuName: 'Skywind',
        },
        NLC: {
            transferSettingIcon: '/public/html/images/footer/nlc_hover.png',
            footerIcon: '/public/html/default_whitelabel/template/images/footer-icon/provider/s5/NLC.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_nlc_of.png',
            menuName: 'NolimitCity',
        },
        BLP: {
            transferSettingIcon: '/public/html/images/footer/blp_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_blp_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_blp_of.png',
            menuName: 'Balakplay',
        },
        WIN: {
            transferSettingIcon: '/public/html/images/footer/win_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_win_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_win_of.png',
            menuName: '568win',
        },
        MTV: {
            transferSettingIcon: '/public/html/images/footer/mtv_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mtv_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mtv_of.png',
            menuName: 'Motivation',
        },
        GMN: {
            transferSettingIcon: '/public/html/images/footer/gmn_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_gmn_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_gmn_of.png',
            menuName: 'Gemini',
        },
        IMN: {
            transferSettingIcon: '/public/html/images/footer/imn_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_imn_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_imn_of.png',
            menuName: 'iMoon',
        },
        TADA: {
            transferSettingIcon: '/public/html/images/footer/tada_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_tada_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_tada_of.png',
            menuName: 'Tada',
        },
        RG: {
            transferSettingIcon: '/public/html/images/footer/rg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_rg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_rg_of.png',
            menuName: 'RelaxGaming',
        },
        MGP2: {
            transferSettingIcon: '/public/html/images/footer/mg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_mg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_mg_of.png',
            menuName: 'MicroGamingPlus2',
        },
        SPB: {
            transferSettingIcon: '/public/html/images/footer/spb_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_spb_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_spb_of.png',
            menuName: 'Spribe',
        },
        SSG: {
            transferSettingIcon: '/public/html/images/footer/ssg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ssg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ssg_of.png',
            menuName: 'SmartSoft Gaming',
        },
        BG: {
            transferSettingIcon: '/public/html/images/footer/bg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_bg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_bg_of.png',
            menuName: 'BigGaming',
        },
        BTG: {
            transferSettingIcon: '/public/html/images/footer/btg_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_btg_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_btg_of.png',
            menuName: 'BTGaming',
        },
        WESPO: {
            launchFromMenu: ['S-WESPO'],
            transferSettingIcon: '/public/html/images/footer/wespo_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_wespo_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_wespo_of.png',
            menuName: 'WESPO',
        },
        ONC: {
            transferSettingIcon: '/public/html/images/footer/onc_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_onc_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_onc_of.png',
            menuName: 'OnCasino',
        },
        CBX: {
            transferSettingIcon: '/public/html/images/footer/cbx_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_cbx_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_cbx_of.png',
            menuName: 'CyberBetx',
        },
        EEAI: {
            transferSettingIcon: '/public/html/images/footer/eeai_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_eeai_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_eeai_of.png',
            menuName: 'EEAI',
        },
        SPL: {
            transferSettingIcon: '/public/html/images/footer/spl_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_spl_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_spl_of.png',
            menuName: 'SuperLive',
        },
        ILU: {
            transferSettingIcon: '/public/html/images/footer/ilu_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_ilu_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_ilu_of.png',
            menuName: 'ILoveU',
        },
        EDVN: {
            transferSettingIcon: '/public/html/images/footer/edvn_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_edvn_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_edvn_of.png',
            menuName: 'EDVN',
        },
        IDN2: {
            transferSettingIcon: '/public/html/images/footer/idn2_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_idn2_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_idn2_of.png',
            menuName: 'IDN2',
        },
        BBS: {
            transferSettingIcon: '/public/html/images/footer/bbs_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_bbs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_bbs_of.png',
            menuName: 'Betby',
        },
        BTRS: {
            transferSettingIcon: '/public/html/images/footer/btrs_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_btrs_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_btrs_of.png',
            menuName: 'BTRS',
        },
        KYP: {
            transferSettingIcon: '/public/html/images/footer/kyp_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_kyp_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_kyp_of.png',
            menuName: 'KY Poker',
        },
        IO: {
            transferSettingIcon: '/public/html/images/footer/io_hover.png',
            footerIcon: '/public/html/images/footer/ftr_brand_io_ov.png',
            footerIconOf: '/public/html/images/footer/ftr_brand_io_of.png',
            menuName: 'IO',
        },
    },
    SPORT_PROVIDER_LIST: {
        provider: [
            {
                id: 1,
                provider: 'BR',
                img: '/public/html/images/sports/bt.jpg',
            },
            {
                id: 2,
                provider: 'CMD',
                img: '/public/html/images/sports/cmd.jpg',
            },
            {
                id: 3,
                provider: 'm8',
                img: '/public/html/images/sports/m8.jpg',
            },
            {
                id: 4,
                provider: 'MB',
                img: '/public/html/images/sports/mb.jpg',
            },
            {
                id: 5,
                provider: 'SBO',
                img: '/public/html/images/sports/sb.jpg',
            },
            {
                id: 6,
                provider: 'PINN',
                img: '/public/html/images/sports/pinn.jpg',
            },
            {
                id: 7,
                provider: 'WESPO',
                img: '/public/html/images/sports/wespo.jpg',
            },
            {
                id: 8,
                provider: 'IMSPO',
                img: '/public/html/images/sports/imspo.jpg',
            },
            {
                id: 8,
                provider: 'WESPO',
                img: '/public/html/images/sports/wespo.jpg',
            },
            {
                id: 9,
                provider: 'BTRS',
                img: '/public/html/images/sports/btrs.jpg',
            },
        ],
    },

    providerLaunchGameFromMenuWithoutLogin: ['CMD', 'BTI', 'BTI2', 'M8', 'PINN', 'WESPO'],
    grouping: ['S', 'ES', 'C', 'SL', 'G', 'F', 'L', 'P', 'RC', 'CF'],

    isAbleLaunchGameFromMenu(menuObj) {
        let found = provider.providers[menuObj.provider];
        if (found) {
            let key = menuObj.category + '-' + menuObj.name;
            return found.launchFromMenu && found.launchFromMenu.indexOf(key) >= 0;
        }
    },

    getHotMatches(countryLanguageKey, viewType) {
        return new Promise(function (resolve) {
            window.SPL_Content.getIbcMatch(countryLanguageKey, viewType).then((result) => {
                if (result && result.ibcUpComingResults) {
                    for (let i = 0; i < result.ibcUpComingResults.length; i++) {
                        // result.ibcUpComingResults[i].countdownMin = calculateTimeDifferenceForLiveTV(result.ibcUpComingResults[i].dateTime, new Date());
                        updateMatchTime(result.ibcUpComingResults[i]);
                    }
                }
                resolve(result);
            });
        });

        function calculateTimeDifferenceForLiveTV(dt1, dt2) {
            let diff = dt1 - dt2;

            if (diff <= 0) {
                return new Date(2010, 0, 1);
            } else {
                let seconds = Math.floor(diff / 1000);
                let minutes = Math.floor(seconds / 60);
                let hours = Math.floor(minutes / 60);
                let days = Math.floor(hours / 24);
                var months = Math.floor(days / 30);

                days = days - months * 30;
                hours = hours - months * 30 * 24 - days * 24;
                minutes = minutes - months * 30 * 24 * 60 - days * 24 * 60 - hours * 60;
                seconds = seconds - months * 30 * 24 * 60 * 60 - days * 24 * 60 * 60 - hours * 60 * 60 - minutes * 60;

                var returnDate = new Date(2010, 0, 0);
                if (days <= 0) {
                    returnDate.setHours(hours);
                    returnDate.setMinutes(minutes);
                    returnDate.setSeconds(seconds);
                } else {
                    returnDate = new Date(2010, 0, 1);
                    returnDate.setDate(days);
                    returnDate.setHours(hours);
                    returnDate.setMinutes(minutes);
                    returnDate.setSeconds(seconds);
                }

                return returnDate;
            }
        }

        // check if expired
        function updateMatchTime(matchDetail) {
            if (matchDetail) {
                let currentTime = new Date();
                let matchTime = new Date(matchDetail.dateTime);

                let tempMatchTime = new Date(matchDetail.dateTime);
                matchDetail.countdownMin = calculateTimeDifferenceForLiveTV(tempMatchTime, new Date());

                let expiredFlag = matchTime - currentTime;
                if (expiredFlag <= 0) {
                    matchDetail.isExpired = true;
                } else {
                    matchDetail.isExpired = false;
                    // Check if more than 1 day
                    // state true = more than 1 day
                    if (matchDetail.countdownMin.getYear() === 109) {
                        matchDetail.state = false;
                    } else {
                        matchDetail.state = true;
                    }
                }
            }
        }
    },

    launchGameFromIndex(vm, providerObj, isFun) {
        providerObj.isFun = isFun;
        const { language, user, screen } = vm.props;

        provider.launchGameFromMenu(providerObj, language, user.isLogin, screen, vm.props);
    },

    async launchGameFromMenu(menuObj, language, isLogin, screen, props, isNavbar) {
        let category = menuObj.category;
        let isMobile = false;
        let gameParam = null;
        let isFun = menuObj.isFun ? menuObj.isFun : false;
        const { features } = props?.portalSettingsReducer?.portalSettings || {};
        const { seamless, menuSlice } = props;
        let _menuObj = JSON.parse(JSON.stringify(menuObj));

        let changeSubmenuRedirectToLandingpage = features.changeSubmenuRedirectToLandingpage ? features.changeSubmenuRedirectToLandingpage : false;
        let forceToRegisterPageWhilePlyGame = features.forceToRegisterPageWhilePlyGame ? features.forceToRegisterPageWhilePlyGame : false;
        let forceToLoginPageWhilePlyGame =
            features.forceToLoginPageWhilePlyGame && features.forceToLoginPageWhilePlyGame[screen.viewType] ? features.forceToLoginPageWhilePlyGame[screen.viewType] : false;
        let seamlessParams = {
            isSeamless: seamless && seamless.isSeamless,
            languageCurrency: language.currencyLang,
        };

        if (_menuObj.provider === 'IDN') {
            category = 'P';
        } else if (_menuObj.provider === 'PS') {
            category = 'P';
        }

        if (_menuObj.category === PROVIDER_CATEGORY.GAME) {
            category = 'G';
        } else if (_menuObj.category && _menuObj.category.toUpperCase() === PROVIDER_CATEGORY.SLOT) {
            category = 'SL';
        } else if (_menuObj.category === PROVIDER_CATEGORY.FISHING) {
            category = 'F';
        } else if (_menuObj.category === PROVIDER_CATEGORY.LOTTERY) {
            category = 'L';
        } else if (_menuObj.category === PROVIDER_CATEGORY.BINGO) {
            category = 'BG';
        }

        let langKey = language.key;
        if (['BTI', 'BTI2'].includes(_menuObj.provider) && langKey === 'km') {
            langKey = 'en';
        }

        if (screen.viewType === 'mobile') {
            if (_menuObj.name && _menuObj.name.includes('_DESKTOP')) {
                isMobile = false;
            } else {
                isMobile = true;
            }
        }

        // special convert if the gameCode is get from merchant_game table
        if (_menuObj.game) {
            _menuObj.gameCode = _menuObj.game;
        }

        if (_menuObj.gameCode) {
            gameParam = {
                domain: window.location.hostname,
                isMobile,
                isFun: isFun,
                isMenuMode: true,
                isMiniGame: false,
                languageKey: language.key,
                provider: _menuObj.provider,
                gameCode: _menuObj.gameCode,
            };
        }

        if (_menuObj.isEsports) {
            gameParam = {
                isEsports: true,
                gameCode: _menuObj.gameCode,
            };
        }

        const currentProviderSettings = (menuSlice?.processedBaseMenuSettings?.subCategoryMenus?.[category] || []).find((item) => item.provider === _menuObj.provider) || {};
        const isOpenInIframe = currentProviderSettings?.isOpenInIframe;
        if (isOpenInIframe) {
            _menuObj = {
                ..._menuObj,
                isOpenInIframe: true,
            };
        }
        if (_menuObj.cardBannerToLanding) {
            props.history.push(_menuObj.landingRoute);
        } else {
            // prompt login
            const { portalSettingsReducer } = props;
            const tempDisableDemo = portalSettingsReducer?.portalSettings?.tempDisableDemo?.[_menuObj.category]?.[_menuObj.provider] && !isLogin;
            if ((!_menuObj.enableLaunchGameBeforeLogin && gameParam && gameParam.isFun === false && isLogin === false) || tempDisableDemo) {
                alertLoginCallback();
            } else {
                window.SPL_Provider.startProviderGameV2(
                    _menuObj.provider,
                    category,
                    langKey ? langKey : language.key,
                    isMobile,
                    isFun,
                    isLogin,
                    alertLoginCallback,
                    gameParam,
                    _menuObj,
                    seamlessParams
                ).then((getProviderUrl) => {
                    if (getProviderUrl && isOpenInIframe) {
                        props.history.push('/launch-game', { iframeUrl: getProviderUrl });
                    }
                });
            }
        }

        function alertLoginCallback() {
            if (forceToLoginPageWhilePlyGame) {
                const prevPathHistory = window.location.href;
                memberService.storePrevPathHistory(prevPathHistory);

                props.history.push('/login');
            } else if (forceToRegisterPageWhilePlyGame) {
                props.history.push('/register');
            } else if (!isMobile && isNavbar && changeSubmenuRedirectToLandingpage && _menuObj && !_menuObj.isLaunchGameFromLanding) {
                props.history.push(_menuObj.landingRoute);
            } else {
                props.dispatch(portalAction.showLoginAlert(true));
            }
        }
    },

    launchGameFromHomePage(gameObj, language, isLogin, screen, props) {
        let copyGameObj = window.SPL_Other.deepCopy(gameObj);

        if (!copyGameObj.provider) {
            copyGameObj.provider = gameObj.providerName;
        }

        const { features } = props?.portalSettingsReducer?.portalSettings || {};
        const { seamless, menuSlice } = props;
        let forceToLoginPageWhilePlyGame =
            features.forceToLoginPageWhilePlyGame && features.forceToLoginPageWhilePlyGame[screen.viewType] ? features.forceToLoginPageWhilePlyGame[screen.viewType] : false;

        let isMobile = false;
        if (screen.viewType === 'mobile') {
            isMobile = true;
        }
        let customParam = {
            domain: window.location.hostname,
            isMobile: screen.isMobile,
            isFun: copyGameObj.isFun ? copyGameObj.isFun : false,
            isMenuMode: true,
            isMiniGame: false,
            languageKey: language.key,
            gameCode: copyGameObj.gameCode,
            provider: copyGameObj.provider,
        };
        let seamlessParams = {
            isSeamless: seamless && seamless.isSeamless,
            languageCurrency: language.currencyLang,
        };

        if (copyGameObj.provider === 'SPB') {
            copyGameObj.category = 'SL';
        }

        if (copyGameObj.category && copyGameObj.category.toUpperCase() === 'SLOT') {
            copyGameObj.category = 'SL';
        }

        if (isLogin === false && copyGameObj.isLaunchGame && provider.providerLaunchGameFromMenuWithoutLogin.indexOf(copyGameObj.provider) >= 0) {
            customParam.isFun = true;
        }

        const currentProviderSettings =
            (menuSlice?.processedBaseMenuSettings?.subCategoryMenus?.[copyGameObj.category] || []).find((item) => item.provider === customParam.provider) || {};
        const isOpenInIframe = currentProviderSettings?.isOpenInIframe;
        if (isOpenInIframe) {
            gameObj.isOpenInIframe = true;
        }

        if (!gameObj.enableLaunchGameBeforeLogin && customParam.isFun === false && isLogin === false) {
            alertLoginCallback();
        } else {
            window.SPL_Provider.startProviderGameV2(
                copyGameObj.provider,
                copyGameObj.category,
                language.key,
                isMobile,
                copyGameObj.isFun,
                isLogin,
                alertLoginCallback,
                customParam,
                gameObj,
                seamlessParams
            ).then((getProviderUrl) => {
                if (getProviderUrl && isOpenInIframe) {
                    props.history.push('/launch-game', { iframeUrl: getProviderUrl });
                }
            });
        }

        function alertLoginCallback() {
            if (forceToLoginPageWhilePlyGame) {
                const prevPathHistory = window.location.href;
                memberService.storePrevPathHistory(prevPathHistory);
                props.history.push('/login');
            } else {
                props.dispatch(portalAction.showLoginAlert(true));
            }
        }
    },

    launchGame(vm, isFun, extraParam, targetCategory) {
        const { features } = window.portalSettings || {};
        const { screen, seamless, language, menuSlice } = vm.props;

        const forceToRegisterPageWhilePlyGame = features?.forceToRegisterPageWhilePlyGame || false;
        const forceToLoginPageWhilePlyGame = features?.forceToLoginPageWhilePlyGame?.[screen?.viewType] || false;

        let seamlessParams = {
            isSeamless: seamless && seamless.isSeamless,
            languageCurrency: language.currencyLang,
        };

        return new Promise(function (resolve) {
            var providerCode = null;
            var category = null;

            // 1. Try processed Redux menu first
            const normalizeProviderName = (value) =>
                String(value ?? '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '');

            const subCategoryMenus = menuSlice?.processedBaseMenuSettings?.subCategoryMenus || {};
            const menus = targetCategory ? subCategoryMenus[targetCategory] || [] : Object.values(subCategoryMenus).flat();
            const currentProvider = normalizeProviderName(vm.state.currentProvider);
            const matches = menus.filter((item) => {
                const routeProviderName = item?.route?.split('/').filter(Boolean).pop();
                return (
                    currentProvider &&
                    item?.provider &&
                    item?.category &&
                    (!targetCategory || item.category === targetCategory) &&
                    normalizeProviderName(routeProviderName) === currentProvider
                );
            });
            const matchedProvider = matches[0];
            const hasAmbiguousMatch = matches.some((item) => item.provider !== matchedProvider.provider || item.category !== matchedProvider.category);
            if (hasAmbiguousMatch) {
                notification.showNotification('warning', 'provider not defined');
                resolve(vm);
                return;
            }

            if (matchedProvider) {
                providerCode = matchedProvider.provider;
                category = matchedProvider.category;
            }

            // 2. Temporary legacy fallback
            if (!providerCode || !category) {
                switch (vm.state.currentProvider) {
                    case 'gggaming':
                        category = 'F';
                        providerCode = 'GG';
                        break;
                    case 'safishing':
                        category = 'F';
                        providerCode = 'SA';
                        break;
                    case 'sgfishing':
                    case 'spadegamingfishinggod':
                        category = 'F';
                        providerCode = 'SG';
                        break;
                    case 'cashfish':
                        category = 'F';
                        providerCode = 'PT2';
                        break;
                    case 'sgfishingwar':
                    case 'spadegamingfishingwar':
                        category = 'F';
                        providerCode = 'SGFW';
                        break;

                    case 'poker':
                        category = 'P';
                        providerCode = 'IDN';
                        break;
                    case 'pokerlanding':
                        category = 'P';
                        providerCode = 'IDN';
                        break;
                    case 'idn':
                        category = 'P';
                        providerCode = 'IDN';
                        break;
                    case 't7':
                        category = 'P';
                        providerCode = 'T7';
                        break;
                    case 'jili':
                        category = 'P';
                        providerCode = 'JL';
                        break;

                    case 'qqkeno':
                        category = 'L';
                        providerCode = 'QQK';
                        break;
                    case 'qqkenothai':
                        category = 'L';
                        providerCode = 'QQKTHAI';
                        break;
                    case 'granddragon':
                        category = 'L';
                        providerCode = 'GDL';
                        break;

                    case 'funkygames':
                        category = 'L';
                        providerCode = 'FG';
                        break;

                    case 'biggaming':
                        category = 'C';
                        providerCode = 'BG';
                        break;
                    case 'dreamgaming':
                        category = 'C';
                        providerCode = 'DG';
                        break;
                    case 'sagaming':
                        category = 'C';
                        providerCode = 'SA';
                        break;

                    case 'asiagaming':
                    case 'asiagaming2':
                    case 'playace':
                        category = 'C';
                        providerCode = 'AG2';
                        break;

                    case 'sexybaccarat':
                    case 'aecasino':
                        category = 'C';
                        providerCode = 'AES'; // updated code overwrite from vm.state.provider
                        break;
                    case 'sexybaccarat2':
                    case 'aecasino2':
                    case 'sexycasino':
                        category = 'C';
                        providerCode = 'AES2'; // updated code overwrite from vm.state.provider
                        break;
                    case 'allbet':
                        category = 'C';
                        providerCode = 'AB';
                        break;
                    case 'allbet2':
                        category = 'C';
                        providerCode = 'AB2';
                        break;
                    case 'evolutiongaming':
                    case 'EVO':
                        category = 'C';
                        providerCode = 'EVO';
                        break;
                    case 'ezugi':
                    case 'ez':
                        category = 'C';
                        providerCode = 'EZ';
                        break;
                    case 'gameplay':
                        category = 'C';
                        providerCode = 'GP';
                        break;
                    case 'playtech':
                        category = 'C';
                        providerCode = 'PT2';
                        break;
                    case 'playtechLive':
                        category = 'C';
                        providerCode = 'PTL';
                        break;
                    case 'playtechLive4':
                        category = 'C';
                        providerCode = 'PTL4';
                        break;
                    case 'playtechLive5':
                        category = 'C';
                        providerCode = 'PTL5';
                        break;
                    case 'gdgaming':
                        category = 'C';
                        providerCode = 'GD';
                        break;
                    case 'ebet':
                        category = 'C';
                        providerCode = 'EBET';
                        break;
                    case 'microGamingPlus':
                        category = 'C';
                        providerCode = 'MGP';
                        break;
                    case 'Microgaming':
                        category = 'C';
                        providerCode = 'MGPL';
                        break;
                    case 'prettygaming':
                        category = 'C';
                        providerCode = 'PTG';
                        break;
                    case 'microGamingPlus2':
                        category = 'C';
                        providerCode = 'MGP2';
                        break;
                    case 'wmcasino':
                    case 'wm':
                    case 'wm-casino':
                        category = 'C';
                        providerCode = 'WM';
                        break;
                    case 'w':
                        category = 'C';
                        providerCode = 'W';
                        break;
                    case 'we':
                        category = 'C';
                        providerCode = 'WE';
                        break;
                    case 'ppl':
                    case 'pragmaticplay':
                        category = 'C';
                        providerCode = 'PPL';
                        break;
                    case 'binl2':
                        category = 'C';
                        providerCode = 'BINL2';
                        break;
                    case 'virtualsports-landing':
                        category = 'S';
                        providerCode = 'BR';
                        break;
                    case 'ufa':
                        category = 'S';
                        providerCode = 'UFA';
                        break;
                    case 'sbo':
                    case 'sbo2':
                        category = 'S';
                        providerCode = 'SBO2';
                        break;
                    case 'cmd368':
                        category = 'S';
                        providerCode = 'CMD';
                        break;
                    case 'ybl':
                        category = 'C';
                        providerCode = 'YBL';
                        break;
                    case 'bk8casino':
                    case '96mcasino':
                        category = 'C';
                        providerCode = 'WE';
                        break;
                    case 'viacasino':
                        category = 'C';
                        providerCode = 'VC';
                        break;
                    case 'motivation':
                    case 'mtv':
                        category = 'C';
                        providerCode = 'MTV';
                        break;
                    case 'oncasino':
                    case 'onc':
                        category = 'C';
                        providerCode = 'ONC';
                        break;
                    case 'afb':
                        category = 'L';
                        providerCode = 'AFB';
                        break;
                    case 'cyberbetx':
                    case 'cbx':
                        category = 'C';
                        providerCode = 'CBX';
                        break;
                    case 'EEAI':
                    case 'eeai':
                        category = 'C';
                        providerCode = 'EEAI';
                        break;
                    case 'MTL':
                        category = 'C';
                        providerCode = 'MTL';
                        break;
                    case 'edvn':
                        category = 'L';
                        providerCode = 'EDVN';
                        break;
                    case 'chg':
                        category = 'C';
                        providerCode = 'CHG';
                        break;
                    default:
                        break;
                }
            }

            if (!providerCode || !category || (targetCategory && category !== targetCategory)) {
                notification.showNotification('warning', 'provider not defined');
            } else {
                var gameObj = {
                    provider: providerCode,
                    category: category,
                };

                const { language, screen, user, menuSlice } = vm.props;
                const currentProviderSettings =
                    (menuSlice?.processedBaseMenuSettings?.subCategoryMenus?.[category] || []).find((item) => item.provider === providerCode) || {};

                let isMobile = false;
                if (screen.viewType === 'mobile') {
                    isMobile = true;
                }
                const isOpenInIframe = currentProviderSettings?.isOpenInIframe;
                if (isOpenInIframe) {
                    extraParam = {
                        ...extraParam,
                        isOpenInIframe: true,
                    };
                }
                window.SPL_Provider.startProviderGameV2(
                    gameObj.provider,
                    gameObj.category,
                    language.key,
                    isMobile,
                    isFun,
                    user.isLogin,
                    alertLoginCallback,
                    null,
                    extraParam,
                    seamlessParams
                ).then((getProviderUrl) => {
                    if (getProviderUrl && isOpenInIframe) {
                        vm.props.history.push('/launch-game', {
                            iframeUrl: getProviderUrl,
                        });

                        resolve(vm);
                    }
                });
            }

            function alertLoginCallback() {
                if (forceToLoginPageWhilePlyGame) {
                    const prevPathHistory = window.location.href;
                    memberService.storePrevPathHistory(prevPathHistory);
                    vm.props.history.push('/login');
                } else if (forceToRegisterPageWhilePlyGame) {
                    vm.props.history.push('/register');
                } else {
                    vm.props.dispatch(portalAction.showLoginAlert(true));
                }
            }

            resolve(vm);
        });
    },

    getCurrentProviderFromURL(vm) {
        return new Promise(function (resolve) {
            var path = window.location.pathname;
            var index = path.lastIndexOf('/');
            path = path
                .slice(index + 1)
                .replace('-', '')
                .toLowerCase();

            if (path.includes('slots')) {
                vm.setState({ category: 'S' });
            } else if (path.includes('casino')) {
                vm.setState({ category: 'C' });
            }

            vm.setState({ currentProvider: path });
            resolve(vm);
        });
    },

    getFooterProviderList(props) {
        let footerIconList = [];

        return new Promise(function (resolve) {
            window.SPL_JsonSettings.getMenuSettingsJson((menuSettings) => {
                const { language, portalSettingsReducer } = props;
                const { countryLanguageKey } = language;

                if (menuSettings && menuSettings[countryLanguageKey] && menuSettings[countryLanguageKey].web) {
                    let webMenu = menuSettings[countryLanguageKey].web;
                    let menus = webMenu.menu.ordering;
                    let submenus = webMenu.submenu;
                    let submenuKeys = Object.keys(submenus);
                    let indexMenus = webMenu.indexMenu;
                    let indexKeys = null;
                    let menuShowingList = [];

                    let sortedSubmenuKeys = [];
                    let tempVM = {};
                    tempVM.props = props;
                    const isSortedSubmenuKeys = portalSettingsReducer?.portalSettings?.features?.sortedSubmenuKeys;
                    const isHideBTIFooterProvider = portalSettingsReducer?.portalSettings?.features?.hideBTIFooterProvider;
                    const footerSettings = commonUtil.getSettingFromPortalSettings({
                        vm: tempVM,
                        settingName: 'footerSettings',
                    });

                    let customFooterProviders = [];
                    let allowCustomFooterProviders = false;
                    if (portalSettingsReducer?.portalSettings?.features?.footerProviderSetting?.customFooterProviders) {
                        customFooterProviders = portalSettingsReducer?.portalSettings?.features?.footerProviderSetting?.customFooterProviders;
                        if (customFooterProviders.length > 0) {
                            allowCustomFooterProviders = true;
                        }
                    }

                    for (let i = 0; i < menus.length; i++) {
                        if (menus[i] === 'Poker' && !isSortedSubmenuKeys) {
                            menuShowingList.push(menus[i]);
                        }

                        if (isSortedSubmenuKeys) {
                            if (PROVIDER_CATEGORY_PREFIX[menus[i].toUpperCase()]) {
                                sortedSubmenuKeys.push(PROVIDER_CATEGORY_PREFIX[menus[i].toUpperCase()]);
                            }
                        }
                    }

                    const submenuKeysToUse = sortedSubmenuKeys.length > 0 ? sortedSubmenuKeys : submenuKeys;
                    for (let i = 0; i < submenuKeysToUse.length; i++) {
                        let submenuList = submenus[submenuKeysToUse[i]].ordering;
                        for (let j = 0; j < submenuList.length; j++) {
                            menuShowingList.push(submenuList[j]);
                        }
                    }

                    if (indexMenus) {
                        indexKeys = Object.keys(indexMenus);
                        for (let i = 0; i < indexKeys.length; i++) {
                            let indexmenuList = indexMenus[indexKeys[i]].ordering;
                            for (let j = 0; j < indexmenuList.length; j++) {
                                menuShowingList.push(indexmenuList[j]);
                            }
                        }
                    }

                    // get all available footer icon list
                    let providerList = provider.providers;
                    let providers = Object.keys(providerList);
                    if (isSortedSubmenuKeys) {
                        for (let i = 0; i < menuShowingList.length; i++) {
                            for (let j = 0; j < providers.length; j++) {
                                if (providers[j] === 'explaination') {
                                    continue;
                                } else if (['BTI', 'BTI2'].includes(providers[j])) {
                                    if (isHideBTIFooterProvider) continue;
                                } else if (footerShowingProvider(providers[j]) === false) {
                                    continue;
                                }

                                let provider = providerList[providers[j]];
                                if (provider.footerIcon && provider.footerIcon !== '') {
                                    let foundShowingMenu = [];
                                    if (menuShowingList[i] === provider.menuName) {
                                        foundShowingMenu.push(menuShowingList[i]);
                                    }

                                    if (foundShowingMenu.length > 0) {
                                        footerIconList.push(provider);
                                    }
                                }
                            }
                        }

                        if (!(footerSettings && footerSettings.showAllProviders)) {
                            footerIconList = footerIconList.filter((element, index, self) => self.indexOf(element) === index);
                        }
                    } else if (allowCustomFooterProviders) {
                        for (let i = 0; i < customFooterProviders.length; i++) {
                            let provider = providerList[customFooterProviders[i].providerCode];
                            if (provider && provider.footerIconOf && provider.footerIconOf !== '' && footerShowingProvider(customFooterProviders[i].providerCode)) {
                                footerIconList.push(provider);
                            }
                        }
                    } else {
                        for (let i = 0; i < providers.length; i++) {
                            if (providers[i] === 'explaination') {
                                continue;
                            }
                            let provider = providerList[providers[i]];
                            if (provider.footerIcon && provider.footerIcon !== '' && footerShowingProvider(providers[i])) {
                                if (footerSettings && footerSettings.showAllProviders) {
                                    footerIconList.push(provider);
                                } else {
                                    let foundShowingMenu = menuShowingList.filter((a) => a === provider.menuName);
                                    if (foundShowingMenu.length > 0) {
                                        footerIconList.push(provider);
                                    }
                                }
                            }
                        }
                    }
                    resolve(footerIconList);
                } else {
                    resolve(footerIconList);
                }
            });
        });

        // if provider too many, the footer will become too long and caused footer misalign
        // hence only will show certain provider
        function footerShowingProvider(provider) {
            switch (provider) {
                case 'SG':
                case 'PT2':
                case 'MGP':
                case 'MGPL':
                case 'MTL':
                case 'MGP2':
                case 'TTG':
                case 'GP':
                case 'AG2':
                case 'AG':
                case 'SCR2':
                case 'SCR':
                case 'AB':
                case 'AB2':
                case 'DG':
                case 'EVO':
                case 'CMD':
                case 'IBC':
                case 'AES':
                case 'AES2':
                case 'MEGA':
                case 'PP':
                case 'PPL':
                case 'EBET':
                case 'IME':
                case 'NS':
                case 'SA':
                case 'QQK':
                case 'WM':
                case 'FG':
                case 'JKR':
                case 'NT':
                case 'KM':
                case 'KM2':
                case 'M8':
                case 'BINL2':
                case 'MT':
                case 'TF':
                case 'CQ9':
                case 'FC':
                case 'JL':
                case 'YGR':
                case 'HC':
                case 'BINS2':
                case 'PGS3':
                case 'PGS':
                case 'RT':
                case 'PG':
                case 'SPB':
                case 'PNG2':
                case 'YBL':
                case 'BTI':
                case 'BTI2':
                case 'PINN':
                case 'AMBS':
                case 'ED':
                case 'IMN':
                case 'TADA':
                case 'NLC':
                case 'RG':
                case 'HB':
                case 'EZ':
                case 'SSG':
                case 'HS2':
                case 'BG':
                    return true;
                default:
                    return false;
            }
        }
    },

    getFooterProviderListWithHotNewProvider(props) {
        let footerIconList = [];
        return new Promise(function (resolve) {
            const { language } = props;
            const { countryLanguageKey } = language;
            window.SPL_JsonSettings.getMenuSettingsJson((menuSettings) => {
                if (menuSettings && menuSettings[countryLanguageKey] && menuSettings[countryLanguageKey]) {
                    let submenu = menuSettings[countryLanguageKey][props.viewType].submenu;
                    let oriProps = props.oriProps;
                    let submenuList = [];
                    let submenuKeys = Object.keys(submenu);
                    let filterMenuName = [];
                    for (let i = 0; i < submenuKeys.length; i++) {
                        window.SPL_Content.getSubmenu(language.countryLanguageKey, 'web', submenuKeys[i], oriProps.user.account).then((submenu) => {
                            for (let j = 0; j < submenu.length; j++) {
                                submenuList.push(submenu[j]);
                                if (i >= submenuKeys.length - 1) {
                                    let hotNewMenu = _removeDuplicate(submenuList);
                                    // get all available footer icon list
                                    let providerList = provider.providers;
                                    let providers = Object.keys(providerList);
                                    for (let k = 0; k < providers.length; k++) {
                                        let provider = providerList[providers[k]];
                                        let foundShowingMenu = hotNewMenu.filter((a) => {
                                            if (a.name === provider.menuName) {
                                                provider.isHotProvider = a.isHotProvider;
                                                provider.isNewProvider = a.isNewProvider;
                                                return true;
                                            }
                                            return false;
                                        });
                                        if (foundShowingMenu.length > 0 && filterMenuName.indexOf(provider.menuName) === -1) {
                                            filterMenuName.push(provider.menuName);
                                            footerIconList.push(provider);
                                        }
                                    }
                                }
                            }
                            resolve(footerIconList);
                        });
                    }
                }
            });
        });

        function _removeDuplicate(submenuList) {
            let uniqueSubmenu = [];

            for (let i = 0; i < submenuList.length; i++) {
                let test = uniqueSubmenu.some((unique) => {
                    if (unique.name === submenuList[i].name) {
                        if (submenuList[i].isHotProvider) {
                            unique.isHotProvider = true;
                        }
                        if (submenuList[i].isNewProvider) {
                            unique.isNewProvider = true;
                        }
                        return true;
                    } else {
                        return false;
                    }
                });
                if (!test) {
                    uniqueSubmenu.push(submenuList[i]);
                }
            }
            return uniqueSubmenu;
        }
    },

    // convert for ibc
    calculateHandicap(matchResult) {
        if (matchResult.handicap) {
            matchResult.homeHandicap = '';
            matchResult.awayHandicap = '';
            if (matchResult.handicap > 0) {
                matchResult.homeHandicap = matchResult.handicap;
                matchResult.awayHandicap = 0;
            } else {
                matchResult.homeHandicap = -matchResult.handicap;
                matchResult.awayHandicap = 0;
            }
        }
        return matchResult;
    },

    getDesktopRecommendedGame(vm, selectedMenuPrefix) {
        window.SPL_Content.getDesktopRecommendedGames().then((res) => {
            let recommendedGames = res.desktopRecommendedGames;
            if (recommendedGames && recommendedGames.constructor === Object) {
                if (recommendedGames[selectedMenuPrefix]) {
                    recommendedGames = recommendedGames[selectedMenuPrefix];
                } else if (recommendedGames['default']) {
                    recommendedGames = recommendedGames['default'];
                }
            }
            if (recommendedGames) {
                vm.setState({ desktopRecommendedGames: recommendedGames });
            }
        });
    },

    getRecentProviderMaintenance(vm) {
        const { currencyLang } = vm.props.language;

        window.SPL_Content.getRecentProviderMaintenance().then((res) => {
            let noProviderMaintenance = true;
            let data = new Array(7).fill(null);

            if (res.length > 0) {
                data = res.filter((a) => a.currency === currencyLang);

                //filtered provider and time
                data = [...new Map(data.map((item) => [item.provider && item.startTime && item.endTime, item])).values()];

                if (data.length > 0) {
                    noProviderMaintenance = false;
                }

                if (data.length < 7) {
                    for (let i = data.length; i < 7; i++) {
                        data.push(null);
                    }
                }
            }

            vm.setState({ maintenanceList: data, noProviderMaintenance });
        });
    },

    filterTransferDropdownMenuType(menuName) {
        switch (menuName) {
            case 'Sportsbook':
            case 'Sports':
                return 'S';
            case 'Casino':
                return 'C';
            case 'Slot':
            case 'Slots':
                return 'SL';
            case 'Poker':
                return 'P';
            case 'Fishing':
                return 'F';
            case 'Lottery':
                return 'L';
            case 'ESportsbook':
            case 'ESports':
            case 'ESport':
                return 'ES';
            case 'Games':
                return 'G';
            case 'Racing':
                return 'RC';
            case 'Cockfight':
                return 'CF';
            default:
                return null;
        }
    },

    // the categoryList is based on category-type.json
    totalCategoryAmount(vm, categoryList) {
        const { wallet } = vm.props;
        let providerList = wallet.providerWallets;
        let foundProviderList = [];
        let categoryBalance = 0;

        if (categoryList) {
            for (let i = 0; i < categoryList.length; i++) {
                let item = categoryList[i];
                let title = providerList && providerList.filter((a) => a.provider === item);
                if (title && title.length > 0) {
                    let foundProvider = {
                        title: title[0],
                        item: item,
                        includeMainWallet: false,
                    };
                    foundProviderList.push(foundProvider);
                }
            }
        }

        for (let i = 0; i < foundProviderList.length; i++) {
            if (foundProviderList[i].title.balance) {
                categoryBalance += foundProviderList[i].title.balance;
            }
        }

        return categoryBalance;
    },

    // Note: V2 categoryList is based on menu/submenu
    totalCategoryAmountV2(vm, categoryList) {
        const { wallet } = vm.props;
        let providerWalletList = wallet.providerWallets;
        let foundProviderWalletList = [];
        let categoryBalance = 0;

        if (categoryList) {
            for (let i = 0; i < categoryList.length; i++) {
                let item = categoryList[i];
                let title = providerWalletList && providerWalletList.filter((a) => a.provider === item.providerPrefix);
                if (title && title.length > 0) {
                    let foundProviderWallet = {
                        data: title[0],
                        includeMainWallet: false,
                    };
                    foundProviderWalletList.push(foundProviderWallet);
                }
            }
        }

        for (let i = 0; i < foundProviderWalletList.length; i++) {
            if (foundProviderWalletList[i].data && foundProviderWalletList[i].data.balance) {
                categoryBalance += foundProviderWalletList[i].data.balance;
            }
        }

        return categoryBalance;
    },

    getProviderWalletBal(vm, provider) {
        const { wallet } = vm.props;

        let providerList = wallet.providerWallets;
        let foundProvider = providerList && providerList.find((e) => e.provider === provider);
        let bal = foundProvider && foundProvider.balance ? foundProvider.balance : 0;
        bal = window.SPL_Other.formatAmount(bal);

        return bal;
    },

    // NOTE: main + providers
    getTotalWalletAmount(vm) {
        const { wallet, seamless } = vm.props;
        const { mainWallet, providerWallets } = wallet;
        const { memberWallet, isSeamless } = seamless || {};

        let totalWalletAmount = 0;

        const mainAmount = (mainWallet && mainWallet.balance) || 0;
        const providerAmount = (providerWallets && providerWallets.length > 0 && providerWallets.reduce((a, b) => a.balance + b.balance)) || 0;

        const seamlessAmount = parseFloat(memberWallet?.mainWalletBalance) || 0;

        totalWalletAmount = isSeamless ? seamlessAmount : mainAmount + providerAmount;
        return totalWalletAmount;
    },
    correctSPSLang(gameLinkUrl, countryLanguageKey) {
        try {
            const url = new URL(gameLinkUrl);
            if (!/lang=/.test(url.search)) {
                let [lang, currency] = countryLanguageKey.split('_');
                lang = lang.toLowerCase();
                if (lang === 'en') {
                    // IMPORTANT: temporarily fix due to sps api returned 400 error while sending lang=en-TH
                    currency = 'US';
                }
                url.searchParams.append('lang', `${lang}-${currency}`);
            }
            return url.toString();
        } catch (error) {
            console.error('Error correcting SPS language:', error);
            return gameLinkUrl;
        }
    },
    getSPSGameLinkUrl({ props }, responseGameLinkUrl) {
        const { portal, menuProps, language } = props || {};
        const { countryLanguageKey } = language || {};
        const superSportsConfig = portal?.settings?.features?.providerPageSettings?.superSports;
        // TODO: temporary hard code for UAT testing, remove when finish testing on UAT
        const gameLinkUrl = superSportsConfig?.tempHardCodeUATUrl || responseGameLinkUrl || superSportsConfig?.targetOrigin || PROVIDER_URL.SPS;

        let maintenanceEndTime = null;
        let isMaintenance = !responseGameLinkUrl;
        const providerUnderMaintenance = menuProps?.maintenanceList?.find((maintenance) => maintenance.provider === 'SPS');
        if (providerUnderMaintenance) {
            isMaintenance = true;
            maintenanceEndTime = providerUnderMaintenance.endTime;
        }

        let resultGameLinkUrl = gameLinkUrl;

        if (isMaintenance) {
            try {
                const maintenanceUrl = new URL(gameLinkUrl);
                maintenanceUrl.pathname = '/maintenance';

                if (maintenanceEndTime) {
                    const maintenanceEndTimestamp = new Date(maintenanceEndTime).getTime();
                    maintenanceUrl.searchParams.set('maintenanceEndTimestamp', maintenanceEndTimestamp);
                }

                resultGameLinkUrl = maintenanceUrl.toString();
            } catch (error) {
                console.error('Error constructing maintenance URL:', error);
                resultGameLinkUrl = `${PROVIDER_URL.SPS}/maintenance`;
            }
        }

        return provider.correctSPSLang(resultGameLinkUrl, countryLanguageKey);
    },

    getCorrectProviderParam,
};

export default provider;
